import { renderBulletButton } from "../helpers/paginationBullets";

class Carousel extends HTMLElement {
  constructor() {
    super();
    this.id = this.dataset.id;
    this.swiperEl = this.querySelector(".swiper");
    this.showNavigation = this.dataset.showNavigation === "true";
    this.spaceBetween = parseInt(this.dataset.spaceBetween, 10);
    this.disableBelowBreakpoint = this.dataset.disableBelowBreakpoint;
    this.disableAboveBreakpoint = this.dataset.disableAboveBreakpoint;
    this.slides = this.querySelectorAll(".swiper-slide");

    window.loadSwiper.then(
      ({ Swiper, Navigation, EffectFade, Pagination, A11y, Autoplay }) => {
        this.Swiper = Swiper;
        this.Navigation = Navigation;
        this.EffectFade = EffectFade;
        this.Pagination = Pagination;
        this.A11y = A11y;
        this.Autoplay = Autoplay;

        if (this.disableBelowBreakpoint || this.disableAboveBreakpoint) {
          this.shouldEnable() && this.initSwiper();
          window.addEventListener("resize", () => this.handleDisableResize());
        } else {
          this.initSwiper();
        }
      },
    );
  }

  initSwiper() {
    const swiperParams = {
      modules: [this.A11y],
      slidesPerView: "auto",
      spaceBetween: this.spaceBetween,
      centeredSlides: this.dataset.centerSlides === "true",
      breakpoints: this.getBreakpointsConfig(),
      // Maintains `swiper-slide-visible`, which syncInert reads.
      watchSlidesProgress: true,
      a11y: {
        prevSlideMessage: this.dataset.a11yPrevious,
        nextSlideMessage: this.dataset.a11yNext,
        firstSlideMessage: this.dataset.a11yFirst,
        lastSlideMessage: this.dataset.a11yLast,
        slideLabelMessage: this.dataset.a11ySlide,
        // Off-screen slides are inert, so focus can never land in one. Swiper's
        // scroll-into-view focus handler would have nothing to react to.
        scrollOnFocus: false,
      },
    };

    swiperParams.on = {
      init: (swiper) => {
        if (this.dataset.fireEvents === "true") {
          this.dispatchCustomEvent("swiper:init", {
            id: this.id,
            swiper: swiper,
          });
        }

        if (this.dataset.navigationAlignmentTarget) {
          this.updateNavPosition(swiper);
        }
      },
      slideChange: (swiper) => {
        if (this.dataset.fireEvents === "true") {
          this.dispatchCustomEvent("swiper:change", {
            id: this.id,
            activeIndex: swiper.activeIndex,
            swiper: swiper,
          });
        }

        if (this.dataset.navigationAlignmentTarget) {
          this.updateNavPosition(swiper);
        }
      },
      resize: (swiper) => {
        if (this.dataset.navigationAlignmentTarget) {
          this.updateNavPosition(swiper);
        }

        this.syncInert(swiper);
      },
    };

    // Every point at which the set of visible slides can change.
    [
      "afterInit",
      "setTranslate",
      "breakpoint",
      "slidesLengthChange",
      "observerUpdate",
      "update",
    ].forEach((event) => {
      swiperParams.on[event] = (swiper) => this.syncInert(swiper);
    });

    if (this.dataset.showNavigation === "true") {
      swiperParams.modules.push(this.Navigation);
      swiperParams.navigation = {
        nextEl: this.querySelector("[data-navigation-next]"),
        prevEl: this.querySelector("[data-navigation-previous]"),
      };
    }

    if (this.dataset.showPagination === "true") {
      swiperParams.modules.push(this.Pagination);
      swiperParams.pagination = {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
        renderBullet: renderBulletButton,
      };
    }

    if (this.dataset.shouldFade === "true") {
      swiperParams.modules.push(this.EffectFade);
      swiperParams.effect = "fade";
      swiperParams.fadeEffect = {
        crossFade: true,
      };
    }

    if (this.dataset.shouldLoop === "true") {
      swiperParams.loop = true;
    }

    if (this.dataset.autoHeight === "true") {
      swiperParams.autoHeight = true;
    }

    if (this.dataset.centerInsufficientSlides === "true") {
      swiperParams.centerInsufficientSlides = true;
    }

    if (this.dataset.autoPlay !== "0") {
      swiperParams.modules.push(this.Autoplay);
      swiperParams.autoplay = {
        delay: parseInt(this.dataset.autoPlay, 10),
      };
    }

    this.swiper = new this.Swiper(this.swiperEl, swiperParams);
  }

  shouldEnable() {
    if (
      this.disableAboveBreakpoint &&
      window.matchMedia(
        `(min-width: ${window.themeBreakpoints[this.disableAboveBreakpoint]}px)`,
      ).matches
    ) {
      return false;
    }

    if (
      this.disableBelowBreakpoint &&
      window.matchMedia(
        `(max-width: ${window.themeBreakpoints[this.disableBelowBreakpoint]}px)`,
      ).matches
    ) {
      return false;
    }

    return true;
  }

  /**
   * Driven off Swiper's translate rather than its transition events, which do
   * not arrive for a drag, a cancelled animation or a 0ms move and would strand
   * `inert` on the wrong slides. Runs per frame while the carousel is moving;
   * `toggleAttribute` no-ops when the state already matches, so the DOM is only
   * touched as slides actually cross in or out of view.
   *
   * Removes off-screen slides from the tab order and the accessibility tree, so
   * keyboard and screen reader users only reach what is actually on screen.
   * `inert` covers every focusable descendant in one attribute, where
   * aria-hidden + tabindex="-1" has to enumerate them and trips the
   * aria-hidden-focus rule when it misses one.
   */
  syncInert(swiper = this.swiper) {
    if (!swiper || swiper.destroyed) return;

    swiper.slides.forEach((slide) => {
      // Partially visible slides are still perceivable, so they stay reachable.
      const isVisible = slide.classList.contains("swiper-slide-visible");

      // Inerting an ancestor of the focused element blurs it, dropping the user
      // back to the top of the document mid-interaction.
      if (!isVisible && slide.contains(document.activeElement)) return;

      slide.toggleAttribute("inert", !isVisible);
    });
  }

  /**
   * Swiper's own `cleanStyles` teardown knows nothing about `inert`, so without
   * this every slide stays unreachable at widths where the carousel is disabled.
   */
  clearInert() {
    this.querySelectorAll(".swiper-slide").forEach((slide) =>
      slide.removeAttribute("inert"),
    );
  }

  handleDisableResize() {
    if (this.shouldEnable()) {
      !this.swiper && this.initSwiper();
    } else {
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.clearInert();
    }
  }

  getBreakpointsConfig() {
    const breakpoints = {};

    // 1. Initial baseline for 0px (Mobile)
    const isCenteredDefault = this.dataset.centerSlides === "true";
    const baseConfig = {
      spaceBetween: this.spaceBetween,
      centeredSlides: isCenteredDefault,
    };

    breakpoints[0] = { ...baseConfig };

    // 2. Sort theme breakpoints numerically (e.g. 640 -> 1024 -> 1440)
    const sortedBreakpoints = Object.entries(window.themeBreakpoints)
      .map(([key, val]) => [key, parseInt(val, 10)])
      .sort(([, a], [, b]) => a - b);

    // 3. Accumulate active state across screen widths
    let currentConfig = { ...baseConfig };

    sortedBreakpoints.forEach(([key, pixelValue]) => {
      const spacingKey = this.convertToDatasetKey(key);

      if (this.dataset[spacingKey] !== undefined) {
        currentConfig.spaceBetween = parseInt(this.dataset[spacingKey], 10);
      }

      if (key === this.dataset.disableCenterSlidesAbove) {
        currentConfig.centeredSlides = false;
      }

      // Always output the accumulated state to prevent Swiper from losing settings on resize
      breakpoints[pixelValue] = { ...currentConfig };
    });

    return breakpoints;
  }

  convertToDatasetKey = (key) => {
    const parts = key.split("-");
    const capitalizedParts = parts.map(
      (part) => part.charAt(0).toUpperCase() + part.slice(1),
    );

    return `bpSpacing${capitalizedParts.join("")}`;
  };

  goToSlide(index) {
    this.swiper?.slideTo(index);
  }

  update() {
    this.swiper?.update();
  }

  dispatchCustomEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      detail: detail,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  updateNavPosition(swiperInstance) {
    const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
    if (!activeSlide) return;

    const imgContainer = activeSlide.querySelector(
      `.${this.dataset.navigationAlignmentTarget}`,
    );
    if (!imgContainer) return;

    const imgHeight = imgContainer.offsetHeight;

    this.style.setProperty("--current-alignment-height", `${imgHeight}px`);
  }
}

if (!customElements.get("component-carousel")) {
  customElements.define("component-carousel", Carousel);
}
